<template>
  <div id="app">
    <article v-if="$license && $userInfo && $licenseExpired" 
      class="message is-danger">
      <div class="message-header">
        <p>License expired</p>
      </div>
      <div class="message-body">
        License expired on {{ $license.expiryDate | formatDateLocale($userInfo.locale, 'short') }}. Please contact
        support to continue using this app.
      </div>
    </article>
    <div v-else>
      <component :is="randomiseSpinner" 
        v-show="showSpinnerRequestCount > 0" 
        :message="spinnerMessage" />
      <app-nav v-if="$userInfo" />
      <router-view v-if="$userInfo || $route.path === signInRouteTypes.SignIn.path || isMobileImageUploader " />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import auth from '@/auth'
import Vuelidate from 'vuelidate'
import GlobalLicenseMixin from '@/components/mixins/global/GlobalLicenseMixin'
import GlobalUserInfoMixin from '@/components/mixins/global/GlobalUserInfoMixin'
import GlobalSpinnerMixin from '@/components/mixins/global/GlobalSpinnerMixin'
import BulmaToast from '@/components/BulmaToast'
import BulmaSnackbar from '@/components/BulmaSnackbar'
import AppNav from './AppNav.vue'
import VueTheMask from 'vue-the-mask'
import cookie from '@/cookie'
import { Circle9, DoubleBounce, LetterCube, Circle8, Socket, Spinner, RandomSpinner } from '@/components/Spinners'
import SignInRouteTypes from '@/views/signin/RouteTypes'
import MobileImageUploaderRouteTypes from '@/views/mobileimageuploader/RouteTypes'

Vue.use(auth)
Vue.use(Vuelidate)
Vue.use(BulmaToast)
Vue.use(BulmaSnackbar)
Vue.use(VueTheMask)
Vue.use(cookie)
Vue.mixin(GlobalLicenseMixin)
Vue.mixin(GlobalUserInfoMixin)
Vue.mixin(GlobalSpinnerMixin)

export default {
  name: 'App',
  components: {
    AppNav,
    Circle9,
    DoubleBounce,
    LetterCube,
    Circle8,
    Socket,
    Spinner
  },
  filters: {
    formatDateLocale(value, locale, format = '2-digit') {
      const date = new Date(Date.parse(`${value}`))
      if (date && !isNaN(date)) {
        const options = { year: 'numeric', month: format, day: '2-digit' }
        if (Intl) {
          return Intl.DateTimeFormat(locale, options).format(date)
        } else {
          return date.toLocaleDateString(locale, options)
        }
      } else {
        return value
      }
    }
  },
  mixins: {
    // DateTimeFiltersMixin
  },
  data: () => {
    return {
      isMobileImageUploader : false
    }
  },
  computed: {
    ...mapGetters(['showSpinnerRequestCount', 'spinnerMessage']),
    randomiseSpinner() {
      return RandomSpinner
    },
    isSignedIn() {
      return this.$userInfo !== null && this.$userInfo !== undefined
    },
    hasJwtToken() {
      const jwtToken = this.$cookie.get(this.$cookie.tokenCookieName)
      // console.log(jwtToken)
      return jwtToken !== '' && jwtToken !== undefined && jwtToken !== null
    },
    errors: function() {
      return this.$store.getters['errors/all']
    },
    hasErrors: function() {
      return this.$store.getters['errors/hasErrors']
    },
    signInRouteTypes() {
      return SignInRouteTypes
    },
    mobileImageUploaderRouteTypes() {
      return MobileImageUploaderRouteTypes
    },
    imageHyperlinkId() {
      var imageId = this.$route.params.id
      if (!imageId) {
        const pathname = window.location.pathname
        if(pathname.indexOf('mobileimageuploader')>0){
          const pathSegments = pathname.split('/')
          imageId = pathSegments[pathSegments.length - 1]
        }
      }
      return imageId
    }
  },
  watch: {
    hasErrors(value) {
      if (value) {
        this.showSnackbarError(0)
        this.$resetSpinner()
      }
    }
  },
  async created() {
    if(window.location.pathname.indexOf('mobileimageuploader')>0){
      this.isMobileImageUploader = true
      this.$router.push(`/mobileimageuploader/${this.imageHyperlinkId}`)
    }
    else if (this.isSignedIn || this.hasJwtToken) {
      this.$showSpinner()
      await this.getUserInfo() // from GlobalUserInfoMixin
      if (this.$route.path !== '/imageuploader') {
        this.$router.push('/imageuploader')
      }
      this.$hideSpinner()
    } else if (this.$route.path !== '/signin') {
      this.$router.push('/signin')
    }
    document.title = this.$route.meta.title
  },
  methods: {
    showSnackbarError(index) {
      const error = this.errors[index]
      const message = error.data ? error.data.message : ''
      this.$snackbar.open({
        message: `${error.statusCode} ${error.message}: <span class="has-text-danger">${message}</span>`,
        type: 'is-danger',
        position: 'is-top',
        actionText: index >= this.errors.length - 1 ? 'Dismiss' : 'Next',
        duration: 10000,
        onAction: () => {
          if (index < this.errors.length - 1) {
            this.showSnackbarError(index + 1)
          }
        }
      })
      if (index === this.errors.length - 1) {
        this.$store.dispatch('errors/purge')
      }
    }
  }
}
</script>

<style lang="scss">
#app {
  overflow: hidden;
  min-height: 100vh; // minimum height to prevent truncation of dropdown menu. Previous value 550px
  // font-family: 'Varela Round', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
#view-container {
  padding: 10px;
}
</style>
